import { useAtom } from 'jotai';
import React, { useEffect, useState } from 'react';
import { Keyboard } from 'react-native';
import {
    BodyLargeEmphasized,
    BodyLargeRegular,
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    Input,
    KeyboardCompatibleBottomSheetTextInput,
    LabelRegular,
    Sheet,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { MONEY_MAP_NAME_MAX_LENGTH } from '../constants';
import {
    deleteRuleSetInMoneyMap,
    editableMoneyMapAtom,
    updateRuleGroupInMoneyMap,
} from '../state/editableMoneyMapAtom';

interface DeleteConfirmSheetProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
}

const DeleteConfirmSheet = ({
    isOpen,
    onClose,
    onConfirm,
}: DeleteConfirmSheetProps) => {
    return (
        <Sheet
            isOpen={isOpen}
            onClose={onClose}
            title="Delete rule set"
            showCloseButton={false}
        >
            <YStack gap={24}>
                <YStack gap={16}>
                    <BodyLargeEmphasized>
                        Are you sure you want to delete this rule set?
                    </BodyLargeEmphasized>
                    <BodyMediumRegular color="$gray1000">
                        You will also delete any rules associated with this rule
                        set.
                    </BodyMediumRegular>
                </YStack>

                <YStack gap={12}>
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        tone="danger"
                        text="Yes, delete rule set and associated rules"
                        onPress={onConfirm}
                    />
                    <Button
                        variant={ButtonVariant.TERTIARY}
                        text="Cancel"
                        onPress={onClose}
                    />
                </YStack>
            </YStack>
        </Sheet>
    );
};

interface MoneyMapRuleSetEditSheetProps {
    isOpen: boolean;
    onClose: () => void;
    ruleSetId: string | null;
    ruleSetName: string | null;
}

export const MoneyMapRuleSetEditSheet = ({
    isOpen,
    onClose,
    ruleSetId,
    ruleSetName,
}: MoneyMapRuleSetEditSheetProps) => {
    const [editableMoneyMap, setEditableMoneyMap] =
        useAtom(editableMoneyMapAtom);
    const [name, setName] = useState('');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    useEffect(() => {
        if (isOpen && ruleSetName != null) {
            setName(ruleSetName);
        }
    }, [isOpen, ruleSetName]);

    const handleSave = () => {
        if (editableMoneyMap == null || ruleSetId == null) return;
        if (name.trim().length === 0) return;

        setEditableMoneyMap(
            updateRuleGroupInMoneyMap(editableMoneyMap, ruleSetId, {
                name: name.trim(),
            }),
        );
        onClose();
    };

    const handleDelete = () => {
        if (editableMoneyMap == null || ruleSetId == null) return;

        setEditableMoneyMap(
            deleteRuleSetInMoneyMap(editableMoneyMap, ruleSetId),
        );
        setShowDeleteConfirm(false);
        onClose();
    };

    if (ruleSetId == null) return null;

    return (
        <>
            <Sheet isOpen={isOpen} onClose={onClose} showCloseButton={false}>
                <YStack $sm={{ minWidth: 400 }} gap={24}>
                    <XStack
                        width="100%"
                        alignItems="center"
                        justifyContent="center"
                        position="relative"
                    >
                        <BodyLargeRegular textAlign="center">
                            Edit rule set
                        </BodyLargeRegular>
                        <View
                            position="absolute"
                            right={0}
                            onPress={() => {
                                setShowDeleteConfirm(true);
                            }}
                            cursor="pointer"
                            animation="button"
                            pressStyle={{ scale: 0.95 }}
                        >
                            <BodyMediumRegular color="$red900">
                                Delete
                            </BodyMediumRegular>
                        </View>
                    </XStack>

                    <YStack gap={8}>
                        <LabelRegular color="$gray1000">
                            Rule set name
                        </LabelRegular>
                        <KeyboardCompatibleBottomSheetTextInput
                            component={Input}
                            value={name}
                            onChangeText={setName}
                            maxLength={MONEY_MAP_NAME_MAX_LENGTH}
                            placeholder="Enter name"
                            returnKeyType="done"
                            onSubmitEditing={() => {
                                Keyboard.dismiss();
                            }}
                            fontWeight="regular"
                        />
                    </YStack>

                    <Button
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.LARGE}
                        text="Save"
                        onPress={handleSave}
                        disabled={name.trim().length === 0}
                    />
                </YStack>
            </Sheet>
            <DeleteConfirmSheet
                isOpen={showDeleteConfirm}
                onClose={() => {
                    setShowDeleteConfirm(false);
                }}
                onConfirm={handleDelete}
            />
        </>
    );
};
